/**
 * useSIPMapInteraction Hook
 *
 * Manages map interaction state and callbacks for the Storm Impact Predictions page.
 * Handles hover state, feature styling, and tooltip content generation.
 */

import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import type { PathOptions } from "leaflet";
import type { StormImpactVariable, GeoJSONFeatureCollection } from "@/types";
import type {
  ForecastDataItem,
  ForecastDataByNodeId,
  ForecastVariableData,
  GeographicNodeWithGeometry,
} from "@/api/types";
import type {
  ColorBin,
  PropertyOption,
  VariableOption,
} from "@/components/maps/insets";
import { nodesToGeoJSONFeatureCollection } from "@/api/types/geography";
import {
  getColorForValue,
  getDefaultStyle,
  DEFAULT_COLOR_BINS,
  DEFAULT_FILL_COLOR,
  getThreatBadgeClass,
} from "@/utils/mapStyling";

export interface UseSIPMapInteractionProps {
  geographicNodesData: GeographicNodeWithGeometry[] | null;
  forecastData: ForecastDataItem[] | null;
  forecastDataMap: ForecastDataByNodeId;
  selectedVariable: StormImpactVariable | undefined;
  availableVariables: StormImpactVariable[];
  selectedModelId?: string;
  selectedWeatherModel?: { acronym?: string; weather_model_acronym?: string };
  selectedDate?: Date;
  selectedRun?: Date;
  isForecastPlaceholder?: boolean;
  forecastLoading?: boolean;
  isModelRunSelectionPending?: boolean;
}

export interface UseSIPMapInteractionReturn {
  // State
  hoveredRegion: string | null;
  tableCollapsed: boolean;
  boxPlotCollapsed: boolean;
  legendCollapsed: boolean;
  selectedTableVariables: string[];

  // Setters
  setHoveredRegion: (region: string | null) => void;
  setTableCollapsed: (collapsed: boolean) => void;
  setBoxPlotCollapsed: (collapsed: boolean) => void;
  setLegendCollapsed: (collapsed: boolean) => void;
  setSelectedTableVariables: (variables: string[]) => void;

  // Derived Data
  mapGeoJSON: GeoJSONFeatureCollection | null;
  colorBins: ColorBin[];
  propertyOptions: PropertyOption[];
  availableTableVariables: VariableOption[];
  insetSubtitle: string;
  geoDataKey: string;
  forecastDataKey: string;
  effectiveVariableId: string | undefined;
  effectivePropertyName: string;
  isModelTransition: boolean;

  // Refs for latest data (used in callbacks)
  forecastDataMapRef: React.MutableRefObject<ForecastDataByNodeId>;
  selectedVariableRef: React.MutableRefObject<StormImpactVariable | undefined>;

  // Callbacks
  getFeatureStyle: (feature: any) => PathOptions;
  generateTooltipContent: (props: any) => string;
  onEachFeature: (feature: any, layer: any) => void;

  // Click handler for drawer - set externally
  onFeatureClick:
    | ((feature: any, forecastData: ForecastDataItem | null) => void)
    | null;
  setOnFeatureClick: (
    handler:
      | ((feature: any, forecastData: ForecastDataItem | null) => void)
      | null,
  ) => void;

  // Double-click handler for drill-down - set externally
  onFeatureDblClick: ((feature: any) => void) | null;
  setOnFeatureDblClick: (handler: ((feature: any) => void) | null) => void;
}

export function useSIPMapInteraction({
  geographicNodesData,
  forecastData,
  forecastDataMap,
  selectedVariable,
  availableVariables,
  selectedModelId,
  selectedWeatherModel,
  selectedDate,
  selectedRun,
  isForecastPlaceholder = false,
  forecastLoading = false,
  isModelRunSelectionPending = false,
}: UseSIPMapInteractionProps): UseSIPMapInteractionReturn {
  // ============================================================================
  // UI State
  // ============================================================================
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);
  const [tableCollapsed, setTableCollapsed] = useState(false);
  const [boxPlotCollapsed, setBoxPlotCollapsed] = useState(false);
  const [legendCollapsed, setLegendCollapsedInternal] = useState(false);
  // Tracks whether the user has explicitly toggled the legend. Once set, the
  // breakpoint listener stops overriding the user's choice.
  const userHasToggledLegendRef = useRef(false);
  /** Table column selection per model_id so switching models shows the right variables (or [] for new model so defaults apply). */
  const [selectedTableVariablesByModel, setSelectedTableVariablesByModel] =
    useState<Record<string, string[]>>({});

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 1023px)");
    const handleChange = () => {
      if (!userHasToggledLegendRef.current) {
        setLegendCollapsedInternal(mediaQuery.matches);
      }
    };
    handleChange();
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const setLegendCollapsed = useCallback((collapsed: boolean) => {
    userHasToggledLegendRef.current = true;
    setLegendCollapsedInternal(collapsed);
  }, []);

  const availableVariableIds = useMemo(
    () => new Set(availableVariables.map((v) => v.variable_id)),
    [availableVariables],
  );

  const selectedTableVariables = useMemo(() => {
    if (!selectedModelId) return [];
    const stored = selectedTableVariablesByModel[selectedModelId] ?? [];
    const filtered = stored.filter((id) => availableVariableIds.has(id));
    return filtered;
  }, [selectedModelId, selectedTableVariablesByModel, availableVariableIds]);

  const setSelectedTableVariables = useCallback(
    (variables: string[]) => {
      if (!selectedModelId) return;
      setSelectedTableVariablesByModel((prev) => ({
        ...prev,
        [selectedModelId]: variables,
      }));
    },
    [selectedModelId],
  );

  // Click handler for opening drawer (set by parent component)
  const [onFeatureClick, setOnFeatureClickState] = useState<
    ((feature: any, forecastData: ForecastDataItem | null) => void) | null
  >(null);

  // Wrapper to properly set function state (React treats functions as updaters)
  const setOnFeatureClick = useCallback(
    (
      handler:
        | ((feature: any, forecastData: ForecastDataItem | null) => void)
        | null,
    ) => {
      setOnFeatureClickState(() => handler);
    },
    [],
  );

  // Double-click handler for drill-down (set by parent component)
  const [onFeatureDblClick, setOnFeatureDblClickState] = useState<
    ((feature: any) => void) | null
  >(null);

  const setOnFeatureDblClick = useCallback(
    (handler: ((feature: any) => void) | null) => {
      setOnFeatureDblClickState(() => handler);
    },
    [],
  );

  // ============================================================================
  // Refs for latest data (used in lazy callbacks to avoid stale closures)
  // ============================================================================
  const forecastDataMapRef = useRef<ForecastDataByNodeId>(forecastDataMap);
  const selectedVariableRef = useRef(selectedVariable);
  const onFeatureClickRef = useRef(onFeatureClick);
  const onFeatureDblClickRef = useRef(onFeatureDblClick);

  const committedModelIdRef = useRef(selectedModelId);
  const selectedModelChanged = selectedModelId !== committedModelIdRef.current;

  const hasImmediateForecastData =
    !isForecastPlaceholder &&
    !forecastLoading &&
    Object.keys(forecastDataMap).length > 0;

  const isModelTransition =
    !!selectedModelId &&
    selectedModelChanged &&
    (isForecastPlaceholder ||
      forecastLoading ||
      (isModelRunSelectionPending && !hasImmediateForecastData));
  if (!isModelTransition && selectedModelId) {
    committedModelIdRef.current = selectedModelId;
  }

  // Update refs SYNCHRONOUSLY during render.
  if (!isModelTransition) {
    forecastDataMapRef.current = forecastDataMap;
  }
  if (!isModelTransition) {
    selectedVariableRef.current = selectedVariable;
  }
  onFeatureClickRef.current = onFeatureClick;
  onFeatureDblClickRef.current = onFeatureDblClick;

  // ============================================================================
  // Derived Data
  // ============================================================================

  // Transform API geographic nodes to GeoJSON FeatureCollection
  const mapGeoJSON: GeoJSONFeatureCollection | null = useMemo(() => {
    if (!geographicNodesData || geographicNodesData.length === 0) {
      return null;
    }
    return nodesToGeoJSONFeatureCollection(
      geographicNodesData,
    ) as GeoJSONFeatureCollection;
  }, [geographicNodesData]);

  // Derive bins from selected variable
  const derivedColorBins: ColorBin[] = useMemo(() => {
    if (!selectedVariable?.bins || selectedVariable.bins.length === 0) {
      return DEFAULT_COLOR_BINS;
    }
    return selectedVariable.bins.map((bin) => ({
      min: bin.bin_min,
      max: bin.bin_max,
      color: bin.bin_color,
      label: bin.bin_label,
    }));
  }, [selectedVariable]);

  const committedColorBinsRef = useRef<ColorBin[]>(derivedColorBins);
  if (!isModelTransition) {
    committedColorBinsRef.current = derivedColorBins;
  }

  const colorBins: ColorBin[] = isModelTransition
    ? committedColorBinsRef.current
    : derivedColorBins;

  const committedVariableIdRef = useRef(selectedVariable?.variable_id);
  if (!isModelTransition) {
    committedVariableIdRef.current = selectedVariable?.variable_id;
  }
  const effectiveVariableId: string | undefined = isModelTransition
    ? committedVariableIdRef.current
    : selectedVariable?.variable_id;

  const committedPropertyNameRef = useRef(
    selectedVariable?.variable_name || "Value",
  );
  if (!isModelTransition) {
    committedPropertyNameRef.current =
      selectedVariable?.variable_name || "Value";
  }
  const effectivePropertyName: string = isModelTransition
    ? committedPropertyNameRef.current
    : (selectedVariable?.variable_name || "Value");

  // Property options for MapPropertySelector (id = variable_id for cross-model uniqueness)
  const propertyOptions: PropertyOption[] = useMemo(() => {
    return availableVariables.map((v) => ({
      id: v.variable_id,
      label: v.variable_name,
      description: v.variable_description || null,
      type: v.variable_type || null,
      units: v.variable_units,
    }));
  }, [availableVariables]);

  // Available variables for MapTableInset column selection (id = variable_id)
  const availableTableVariables: VariableOption[] = useMemo(() => {
    return availableVariables.map((v) => ({
      id: v.variable_id,
      slug: v.variable_slug,
      name: v.variable_name,
      units: v.variable_units || null,
      order: v.variable_order,
      variableType:
        typeof v.variable_type === "string"
          ? v.variable_type.toLowerCase()
          : undefined,
    }));
  }, [availableVariables]);

  // Build subtitle for insets
  const insetSubtitle = useMemo(() => {
    const parts: string[] = [];
    if (selectedWeatherModel) {
      const acronym =
        selectedWeatherModel.acronym ||
        selectedWeatherModel.weather_model_acronym;
      if (acronym) parts.push(acronym);
    }
    if (selectedDate) {
      parts.push(
        `Valid for ${selectedDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`,
      );
    }
    if (selectedRun) {
      parts.push(
        `${selectedRun.toLocaleDateString("en-US", { month: "short", day: "numeric" })} ${selectedRun.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })} Model Run`,
      );
    }
    return parts.join(" • ");
  }, [selectedWeatherModel, selectedDate, selectedRun]);

  // Generate stable keys for GeoJSON layer
  const geoDataKey = useMemo(() => {
    if (!mapGeoJSON || mapGeoJSON.features.length === 0) {
      return "empty";
    }
    const firstId = mapGeoJSON.features[0]?.properties?.id || "f0";
    const lastId =
      mapGeoJSON.features[mapGeoJSON.features.length - 1]?.properties?.id ||
      "fn";
    return `${firstId}-${lastId}-${mapGeoJSON.features.length}`;
  }, [mapGeoJSON]);

  const derivedForecastDataKey = useMemo(() => {
    if (!forecastData || forecastData.length === 0) {
      return "no-forecast";
    }
    const firstItem = forecastData[0];
    return `${firstItem.id}-${firstItem.run}`;
  }, [forecastData]);

  const committedForecastDataKeyRef = useRef(derivedForecastDataKey);
  if (!isModelTransition) {
    committedForecastDataKeyRef.current = derivedForecastDataKey;
  }

  const forecastDataKey = isModelTransition
    ? committedForecastDataKeyRef.current
    : derivedForecastDataKey;

  // ============================================================================
  // Map Callbacks
  // ============================================================================

  // Style function for GeoJSON features
  const getFeatureStyle = useCallback(
    (feature: any): PathOptions => {
      const isHovered = feature?.properties?.id === hoveredRegion;
      const nodeId = feature?.properties?.id;

      const currentForecastDataMap = forecastDataMapRef.current;
      const currentSelectedVariable = selectedVariableRef.current;

      const nodeForecast = nodeId ? currentForecastDataMap[nodeId] : null;

      let fillColor = DEFAULT_FILL_COLOR;
      if (nodeForecast && currentSelectedVariable) {
        const variableData = nodeForecast.variable_values.find(
          (v) => v.variable_id === currentSelectedVariable.variable_id,
        );
        if (variableData?.value != null) {
          const numericValue = parseFloat(String(variableData.value));
          if (!isNaN(numericValue)) {
            fillColor = getColorForValue(numericValue, colorBins);
          }
        }
      }

      return {
        color: isHovered ? "#6b7280" : "#9ca3af",
        weight: isHovered ? 3 : 1.5,
        opacity: 1,
        fillColor,
        fillOpacity: isHovered ? 0.85 : 0.6,
      };
    },
    [hoveredRegion, colorBins],
  );

  // Generate tooltip content for map features
  const generateTooltipContent = useCallback((props: any): string => {
    const featureName =
      props.name || props.area_name || props.slug || "Geographic Feature";
    const resolution = props.resolution || null;
    const nodeId = props.id;

    const currentForecastDataMap = forecastDataMapRef.current;
    const currentSelectedVariable = selectedVariableRef.current;

    const nodeForecast = nodeId ? currentForecastDataMap[nodeId] : null;
    const hasData = !!nodeForecast;

    let selectedVariableValue: string | null = null;
    let selectedVariableUnits: string | null = null;
    let selectedVariableName: string | null = null;

    if (nodeForecast && currentSelectedVariable) {
      const variableData = nodeForecast.variable_values.find(
        (v) => v.variable_id === currentSelectedVariable.variable_id,
      );
      if (variableData) {
        const numValue = parseFloat(String(variableData.value));
        selectedVariableValue = !isNaN(numValue)
          ? numValue.toLocaleString()
          : String(variableData.value);
        selectedVariableUnits = variableData.variable_units;
        selectedVariableName = variableData.variable_name;
      }
    }

    const activeThreats =
      nodeForecast?.variable_values.filter(
        (v: ForecastVariableData) =>
          v.variable_type === "threat" && parseFloat(String(v.value)) === 1,
      ) || [];

    let content = '<div class="weather-threat-tooltip">';

    // Header section
    content +=
      `<div class="threat-tooltip-header">` +
      `<h3 class="threat-tooltip-title">${featureName}</h3>` +
      (resolution
        ? `<div class="resolution-info">Resolution: ${resolution}</div>`
        : "") +
      `</div>`;

    // Selected variable section
    content += '<div class="selected-variable-section">';
    if (hasData && selectedVariableValue !== null) {
      content +=
        '<div class="selected-variable-display">' +
        `<span class="selected-variable-label">${selectedVariableName || "VALUE"}</span>` +
        `<span class="selected-variable-data">${selectedVariableValue}</span>` +
        `<span class="selected-variable-units">${selectedVariableUnits || ""}</span>` +
        "</div>";
    } else {
      content += '<div class="no-data-available">No Data Available</div>';
    }
    content += "</div>";

    // Weather Threats section
    content += '<div class="threat-tooltip-section">';
    content += '<h4 class="threat-tooltip-section-title">WEATHER THREATS</h4>';

    if (activeThreats.length > 0) {
      content += '<div class="threat-indicators-container">';
      activeThreats.forEach((threat: ForecastVariableData) => {
        const badgeClass = getThreatBadgeClass(threat.variable_slug);
        content += `<span class="threat-badge ${badgeClass}">${threat.variable_name}</span>`;
      });
      content += "</div>";
    } else {
      content +=
        '<div class="no-threats-simple">No major weather threats expected</div>';
    }
    content += "</div>";

    content += "</div>";
    return content;
  }, []);

  // Feature interaction handler
  const onEachFeature = useCallback(
    (feature: any, layer: any) => {
      const props = feature.properties;

      layer.bindPopup(() => generateTooltipContent(props), {
        maxWidth: 320,
        minWidth: 280,
        autoPan: false,
        closeButton: false,
        className: "weather-threat-popup",
      });

      layer.on({
        mouseover: (e: any) => {
          setHoveredRegion(props.id);
          layer.setStyle({
            weight: 3,
            color: "#6b7280",
            fillOpacity: 0.85,
          });
          layer.openPopup(e.latlng);
        },
        mouseout: () => {
          setHoveredRegion(null);
          const currentForecastDataMap = forecastDataMapRef.current;
          const currentSelectedVariable = selectedVariableRef.current;
          const nodeId = props.id;
          const nodeForecast = nodeId ? currentForecastDataMap[nodeId] : null;

          let fillColor = DEFAULT_FILL_COLOR;
          if (nodeForecast && currentSelectedVariable) {
            const variableData = nodeForecast.variable_values.find(
              (v) => v.variable_id === currentSelectedVariable.variable_id,
            );
            if (variableData?.value != null) {
              const numericValue = parseFloat(String(variableData.value));
              if (!isNaN(numericValue)) {
                fillColor = getColorForValue(numericValue, colorBins);
              }
            }
          }

          layer.setStyle(getDefaultStyle(fillColor));
          layer.closePopup();
        },
        mousemove: (e: any) => {
          layer.getPopup()?.setLatLng(e.latlng);
        },
        click: () => {
          // Handle click to open drawer with feature details
          const currentOnFeatureClick = onFeatureClickRef.current;
          const currentForecastDataMap = forecastDataMapRef.current;
          const nodeId = props.id;
          const nodeForecast = nodeId ? currentForecastDataMap[nodeId] : null;

          if (currentOnFeatureClick) {
            currentOnFeatureClick(feature, nodeForecast);
          }
        },
        dblclick: (e: any) => {
          // Prevent Leaflet's default map zoom on double-click
          e.originalEvent.stopPropagation();
          e.originalEvent.preventDefault();
          const currentOnFeatureDblClick = onFeatureDblClickRef.current;
          if (currentOnFeatureDblClick) {
            currentOnFeatureDblClick(feature);
          }
        },
      });
    },
    [generateTooltipContent, colorBins],
  );

  // ============================================================================
  // Return
  // ============================================================================
  return {
    // State
    hoveredRegion,
    tableCollapsed,
    boxPlotCollapsed,
    legendCollapsed,
    selectedTableVariables,

    // Setters
    setHoveredRegion,
    setTableCollapsed,
    setBoxPlotCollapsed,
    setLegendCollapsed,
    setSelectedTableVariables,

    // Derived Data
    mapGeoJSON,
    colorBins,
    propertyOptions,
    availableTableVariables,
    insetSubtitle,
    geoDataKey,
    forecastDataKey,
    effectiveVariableId,
    effectivePropertyName,
    isModelTransition,

    // Refs
    forecastDataMapRef,
    selectedVariableRef,

    // Callbacks
    getFeatureStyle,
    generateTooltipContent,
    onEachFeature,

    // Click handler for drawer
    onFeatureClick,
    setOnFeatureClick,

    // Double-click handler for drill-down
    onFeatureDblClick,
    setOnFeatureDblClick,
  };
}
